import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Database,
  HelpCircle,
  Plus,
  Settings,
  Shield,
  Sparkles,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { cn } from "@repo/core";
import { useT } from "../i18n/useT";
import { ensureDefaultCategoriesOnly, resetAndSeed } from "../lib/db";

const ONBOARD_KEY = "budgetos-onboarding-done";

const STEP_ICONS = [Shield, Plus, Settings, Database, HelpCircle];

export function hasCompletedOnboarding(): boolean {
  return localStorage.getItem(ONBOARD_KEY) === "1";
}

export function resetOnboardingFlag() {
  localStorage.removeItem(ONBOARD_KEY);
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** First launch only — sample vs empty before tour steps. Replay from Help skips this. */
  showDataChoice?: boolean;
}

type Phase = "choice" | "tour";

export function OnboardingTour({ open, onClose, showDataChoice = false }: Props) {
  const { t, locale } = useT();
  const [phase, setPhase] = useState<Phase>(showDataChoice ? "choice" : "tour");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPhase(showDataChoice ? "choice" : "tour");
      setStep(0);
      setBusy(false);
    }
  }, [open, showDataChoice]);

  const finish = () => {
    localStorage.setItem(ONBOARD_KEY, "1");
    onClose();
  };

  /** Dismiss / skip choice → empty app (categories only), never demo. */
  const dismissAsEmpty = async () => {
    setBusy(true);
    try {
      await ensureDefaultCategoriesOnly(locale);
    } finally {
      setBusy(false);
      setPhase("tour");
      setStep(0);
    }
  };

  const chooseSample = async () => {
    setBusy(true);
    try {
      await resetAndSeed(locale);
    } finally {
      setBusy(false);
      setPhase("tour");
      setStep(0);
    }
  };

  const chooseEmpty = async () => {
    setBusy(true);
    try {
      await ensureDefaultCategoriesOnly(locale);
    } finally {
      setBusy(false);
      setPhase("tour");
      setStep(0);
    }
  };

  const Icon = STEP_ICONS[step];
  const isLast = step === STEP_ICONS.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              if (busy) return;
              if (phase === "choice") void dismissAsEmpty().then(finish);
              else finish();
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="relative z-10 w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
          >
            {phase === "choice" ? (
              <>
                <div className="flex items-center justify-between px-5 pt-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("tour.choice.eyebrow")}
                  </p>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void dismissAsEmpty().then(finish)}
                    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
                    aria-label={t("tour.skip")}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="px-5 pb-5 pt-2 space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                    <Sparkles size={22} />
                  </div>
                  <div>
                    <h2
                      className="text-xl text-foreground"
                      style={{ fontFamily: "var(--font-display, inherit)" }}
                    >
                      {t("tour.choice.title")}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {t("tour.choice.body")}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 pt-1">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void chooseSample()}
                      className="app-btn min-h-[48px] justify-center text-sm"
                    >
                      <Database size={15} />
                      {t("tour.choice.sample")}
                    </button>
                    <p className="text-[11px] text-muted-foreground px-0.5 -mt-1 mb-1">
                      {t("tour.choice.sampleDesc")}
                    </p>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void chooseEmpty()}
                      className="app-btn-outline min-h-[48px] justify-center text-sm"
                    >
                      {t("tour.choice.empty")}
                    </button>
                    <p className="text-[11px] text-muted-foreground px-0.5 -mt-1">
                      {t("tour.choice.emptyDesc")}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between px-5 pt-4">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("tour.step", { n: step + 1, total: STEP_ICONS.length })}
                  </p>
                  <button
                    type="button"
                    onClick={finish}
                    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
                    aria-label={t("tour.skip")}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="px-5 pb-5 pt-2 space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h2
                      className="text-xl text-foreground"
                      style={{ fontFamily: "var(--font-display, inherit)" }}
                    >
                      {t(`tour.s${step}.title`)}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {t(`tour.s${step}.body`)}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    {STEP_ICONS.map((_, i) => (
                      <div
                        key={i}
                        className={cn(
                          "h-1 flex-1 rounded-full",
                          i <= step ? "bg-primary" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2 pt-1">
                    {step > 0 && (
                      <button
                        type="button"
                        onClick={() => setStep((s) => s - 1)}
                        className="app-btn-outline flex-1 min-h-[44px]"
                      >
                        {t("tour.back")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
                      className="app-btn flex-1 min-h-[44px]"
                    >
                      {isLast ? (
                        <>
                          <Check size={14} /> {t("tour.done")}
                        </>
                      ) : (
                        <>
                          {t("tour.next")} <ArrowRight size={14} />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
